import { authStore } from './authStore';
import { supabase } from './supabaseClient';
import { addSyncDiagnostic } from './syncDiagnostics';
import type { MarkerNoteFormat, MarkerSheetData } from './types';

export interface PersonalMarkerNoteCloudValue {
  markerClientId: string;
  tabId: string;
  content: string;
  format: MarkerNoteFormat;
  sheetData: MarkerSheetData;
  updatedAt: string;
}

interface PendingPersonalNoteWrite {
  songSupabaseId: string;
  markerClientId: string;
  tabId: string;
  content: string;
  format: MarkerNoteFormat;
  sheetData: MarkerSheetData;
}

const PENDING_STORAGE_KEY = 'maximumBand.personalNotes.pending.v1';
const DEBOUNCE_MS = 700;
const pending = new Map<string, PendingPersonalNoteWrite>();
const timers = new Map<string, number>();
let retryListenerInstalled = false;

function writeKey(value: Pick<PendingPersonalNoteWrite, 'songSupabaseId' | 'markerClientId' | 'tabId'>): string {
  return `${value.songSupabaseId}:${value.markerClientId}:${value.tabId}`;
}

function sheetTextCellCount(sheetData: MarkerSheetData): number {
  return sheetData.rows.reduce((total, row) => total + row.filter((cell) => cell.text.trim().length > 0).length, 0);
}

function persistPending(): void {
  try {
    localStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify([...pending.values()]));
  } catch {
    // The local note itself is still stored by the existing UI. A queue
    // persistence failure must never make note editing fail.
  }
}

function restorePending(): void {
  if (pending.size > 0) return;
  try {
    const raw = localStorage.getItem(PENDING_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return;
    for (const value of parsed) {
      if (!value || typeof value !== 'object') continue;
      const candidate = value as Partial<PendingPersonalNoteWrite>;
      if (
        typeof candidate.songSupabaseId !== 'string' ||
        typeof candidate.markerClientId !== 'string' ||
        typeof candidate.tabId !== 'string' ||
        typeof candidate.content !== 'string' ||
        (candidate.format !== 'text' && candidate.format !== 'sheet') ||
        !candidate.sheetData ||
        !Array.isArray(candidate.sheetData.rows)
      ) continue;
      const restored = candidate as PendingPersonalNoteWrite;
      pending.set(writeKey(restored), restored);
    }
  } catch {
    // Ignore malformed/stale queue data.
  }
}

async function resolveCloudMarkerId(songSupabaseId: string, markerClientId: string): Promise<{ markerId: string | null; error: string | null }> {
  if (!supabase) return { markerId: null, error: 'Supabase is not configured in this build.' };
  const { data, error } = await supabase
    .from('markers')
    .select('id')
    .eq('song_id', songSupabaseId)
    .eq('client_id', markerClientId)
    .maybeSingle();
  if (error) return { markerId: null, error: error.message };
  return { markerId: data ? (data.id as string) : null, error: null };
}

async function pushOne(value: PendingPersonalNoteWrite): Promise<boolean> {
  if (!supabase || !navigator.onLine) {
    addSyncDiagnostic({
      level: 'warn',
      source: 'my-notes-upload',
      message: 'Upload skipped before Supabase write.',
      details: {
        online: navigator.onLine,
        supabaseConfigured: Boolean(supabase),
        songSupabaseId: value.songSupabaseId,
        markerClientId: value.markerClientId,
        tabId: value.tabId,
      },
    });
    return false;
  }
  const session = authStore.getSession();
  if (!session) {
    addSyncDiagnostic({
      level: 'warn',
      source: 'my-notes-upload',
      message: 'Upload skipped because no auth session is active.',
      details: { songSupabaseId: value.songSupabaseId, markerClientId: value.markerClientId, tabId: value.tabId },
    });
    return false;
  }

  const markerResult = await resolveCloudMarkerId(value.songSupabaseId, value.markerClientId);
  if (!markerResult.markerId) {
    addSyncDiagnostic({
      level: 'warn',
      source: 'my-notes-upload',
      message: 'Upload could not find the Supabase marker row for this local marker.',
      details: {
        songSupabaseId: value.songSupabaseId,
        markerClientId: value.markerClientId,
        tabId: value.tabId,
        error: markerResult.error,
      },
    });
    return false;
  }

  const { error } = await supabase.from('personal_marker_notes').upsert(
    {
      user_id: session.user.id,
      marker_id: markerResult.markerId,
      tab_id: value.tabId,
      content: value.content,
      format: value.format,
      sheet_data: value.sheetData,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id,marker_id,tab_id' },
  );
  addSyncDiagnostic({
    level: error ? 'error' : 'info',
    source: 'my-notes-upload',
    message: error ? 'Supabase rejected My Notes upload.' : 'Uploaded My Notes row to Supabase.',
    details: {
      songSupabaseId: value.songSupabaseId,
      markerClientId: value.markerClientId,
      supabaseMarkerId: markerResult.markerId,
      tabId: value.tabId,
      format: value.format,
      contentLength: value.content.length,
      sheetRows: value.sheetData.rows.length,
      sheetTextCells: sheetTextCellCount(value.sheetData),
      error: error?.message ?? null,
    },
  });
  return !error;
}

async function flushKey(key: string): Promise<void> {
  const value = pending.get(key);
  if (!value) return;
  if (await pushOne(value)) {
    // Only clear if this exact value is still the newest queued write. An
    // edit may have arrived while the request was in flight.
    if (pending.get(key) === value) pending.delete(key);
    persistPending();
  }
}

export async function flushPendingPersonalNotes(): Promise<void> {
  restorePending();
  await Promise.all([...pending.keys()].map((key) => flushKey(key)));
}

/**
 * Queue the latest local value for background cloud persistence. The caller
 * should save locally first; this function is deliberately best-effort and
 * never blocks note editing. Writes are debounced and the newest value is
 * persisted across reloads until Supabase accepts it.
 */
export function queuePersonalMarkerNote(value: PendingPersonalNoteWrite): void {
  restorePending();
  const key = writeKey(value);
  pending.set(key, value);
  persistPending();
  addSyncDiagnostic({
    level: 'info',
    source: 'my-notes-queue',
    message: 'Queued My Notes for Supabase upload.',
    details: {
      songSupabaseId: value.songSupabaseId,
      markerClientId: value.markerClientId,
      tabId: value.tabId,
      format: value.format,
      contentLength: value.content.length,
      sheetRows: value.sheetData.rows.length,
      sheetTextCells: sheetTextCellCount(value.sheetData),
      pendingWrites: pending.size,
    },
  });

  const oldTimer = timers.get(key);
  if (oldTimer !== undefined) window.clearTimeout(oldTimer);
  timers.set(key, window.setTimeout(() => {
    timers.delete(key);
    void flushKey(key);
  }, DEBOUNCE_MS));

  if (!retryListenerInstalled) {
    retryListenerInstalled = true;
    window.addEventListener('online', () => void flushPendingPersonalNotes());
    authStore.subscribe((session) => {
      if (session) void flushPendingPersonalNotes();
    });
  }
}

/**
 * Pull this user's personal notes for one cloud song. Results are keyed by
 * stable local marker client id so the caller can merge them into the
 * existing local note representation without replacing local audio/song
 * records.
 */
export async function loadPersonalMarkerNotes(songSupabaseId: string): Promise<{ notes: PersonalMarkerNoteCloudValue[]; error: string | null }> {
  if (!supabase) return { notes: [], error: 'Supabase is not configured in this build.' };
  const session = authStore.getSession();
  if (!session) return { notes: [], error: 'Sign in to sync personal notes.' };

  const { data: markerRows, error: markerError } = await supabase
    .from('markers')
    .select('id, client_id')
    .eq('song_id', songSupabaseId)
    .not('client_id', 'is', null);
  if (markerError) {
    addSyncDiagnostic({
      level: 'error',
      source: 'my-notes-pull',
      message: 'Could not read cloud markers for My Notes pull.',
      details: { songSupabaseId, error: markerError.message },
    });
    return { notes: [], error: markerError.message };
  }

  const markerById = new Map<string, string>();
  for (const row of markerRows ?? []) {
    if (row.client_id) markerById.set(row.id as string, row.client_id as string);
  }
  if (markerById.size === 0) {
    addSyncDiagnostic({
      level: 'warn',
      source: 'my-notes-pull',
      message: 'No cloud markers with stable client IDs were found for this song.',
      details: { songSupabaseId },
    });
    return { notes: [], error: null };
  }

  const { data, error } = await supabase
    .from('personal_marker_notes')
    .select('marker_id, tab_id, content, format, sheet_data, updated_at')
    .eq('user_id', session.user.id)
    .in('marker_id', [...markerById.keys()]);
  if (error) {
    addSyncDiagnostic({
      level: 'error',
      source: 'my-notes-pull',
      message: 'Could not read personal note rows from Supabase.',
      details: { songSupabaseId, cloudMarkerCount: markerById.size, error: error.message },
    });
    return { notes: [], error: error.message };
  }

  const notes: PersonalMarkerNoteCloudValue[] = [];
  for (const row of data ?? []) {
    const markerClientId = markerById.get(row.marker_id as string);
    if (!markerClientId) continue;
    notes.push({
      markerClientId,
      tabId: row.tab_id as string,
      content: (row.content as string) ?? '',
      format: row.format === 'sheet' ? 'sheet' : 'text',
      sheetData: (row.sheet_data as MarkerSheetData) ?? { rows: [] },
      updatedAt: row.updated_at as string,
    });
  }
  addSyncDiagnostic({
    level: 'info',
    source: 'my-notes-pull',
    message: 'Pulled personal note rows for one cloud song.',
    details: {
      songSupabaseId,
      cloudMarkerCount: markerById.size,
      noteRows: notes.length,
      myNotesRows: notes.filter((note) => note.tabId === 'my-notes').length,
      markerClientIds: notes.map((note) => note.markerClientId).join(', '),
    },
  });
  return { notes, error: null };
}
